/**
 * useHandleBookingCreated — what the launcher does with a create outcome
 * (BON-628). Split out of `CreateBookingLauncherProvider` to stay under the
 * repo's line cap.
 */
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { usePermissions } from '../../../hooks/usePermissions';
import { useAppMode } from '../../../shared/nav/use-app-mode';
import { MODE_PERMISSION } from '../../../shared/nav/viewMode';
import { useToast } from '../../../shared/toast/toastContext';
import { READ_BOOKINGS_PERMISSIONS } from '../bookingPermissions';
import { notifyBookingCreated } from '../bookingsRefreshBus';

import { resolveCreatedOutcome } from './CreateBookingLauncherOutcome';
import type { CreateBookingSuccess } from './CreateBookingModalHelpers';

export function useHandleBookingCreated(
  closeModal: () => void,
  setEmailFailedFor: (email: string | null) => void,
): (outcome: CreateBookingSuccess) => void {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { mode } = useAppMode();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  return useCallback(
    (outcome: CreateBookingSuccess) => {
      closeModal();
      notifyBookingCreated({ kind: outcome.kind });

      // Guarded on the permission to read bookings in admin mode AND on
      // actually being in admin mode right now — from /me, confirm without
      // leaving the workspace the viewer is in.
      const canNavigate =
        mode === 'admin' &&
        hasAnyPermission(...READ_BOOKINGS_PERMISSIONS) &&
        hasPermission(MODE_PERMISSION.admin);

      const effects = resolveCreatedOutcome(outcome, canNavigate);
      if (effects.navigateTo) {
        const here = `${location.pathname}${location.search}`;
        navigate(effects.navigateTo, {
          replace: effects.navigateTo === here,
          state: { focusHeading: true },
        });
      }
      if (effects.toast) showToast(effects.toast);
      if (effects.emailFailedFor) setEmailFailedFor(effects.emailFailedFor);
    },
    [
      closeModal,
      setEmailFailedFor,
      mode,
      hasAnyPermission,
      hasPermission,
      location.pathname,
      location.search,
      navigate,
      showToast,
    ],
  );
}
